import React, { useState, useContext } from "react";
import { useMutation } from "react-query";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import ListItemSecondaryAction from "@mui/material/ListItemSecondaryAction";
import Chip from "@mui/material/Chip";
import Box from "@mui/material/Box";

import MoreVertIcon from "@mui/icons-material/MoreVert";

import * as api from "../../services";
import AppContext from "../../appContext";
import ClientsSkeleton from "./ClientSkeleton";
import ClientAvatar from "./ClientAvatar";
import clientOptions from "./clientOptions";
import { useTranslation } from "react-i18next";
import InviteButton from "./InviteButton";
import PairButton from "./PairButton";
import { useHistory, useLocation } from "react-router-dom";

function ClientList({
  clients,
  isLoading,
  testDevice,
  handleOpenClientMenu,
  refetchClients,
}) {
  const { t } = useTranslation();
  const theme = useTheme();
  const location = useLocation();
  const history = useHistory();
  const mobile = useMediaQuery(theme.breakpoints.down("lg"));
  const {
    app: {
      client: { id: clientId },
      project: { id: projectId },
      sessionId,
    },
    dispatch,
  } = useContext(AppContext); // clientId => is client selected?
  const [clientMouseOver, setClientMouseOver] = useState(null); // display more options icon

  // server
  const makeClientActive = useMutation(
    (id) => api.postStatus({ clientId: id, status: 1, projectId, sessionId }),
    { onSuccess: refetchClients }
  );

  const handleDrawerClose = () =>
    dispatch({ type: "SET_OPEN", payload: false });
  const handleClientClick = (client) => {
    //console.log("Client selected with id= " + client.id);
    dispatch({ type: "SET_CLIENT", payload: client });
    if (location.pathname === "/home") history.push("/timeline"); // on home screen, redirect user to timeline
    if (client.new) makeClientActive.mutate(client.id);
    if (mobile) handleDrawerClose();
  };

  const handleMouseOver = (client) => setClientMouseOver(client);

  if (isLoading) return <ClientsSkeleton />;

  if (clients?.length > 0)
    return (
      <List>
        {clients.map((item) => (
          <ListItem
            id={`ClientId${item.id}`}
            button
            selected={item.id === clientId}
            key={item.id}
            onClick={() => handleClientClick(item)}
            onMouseOver={() => handleMouseOver(item)}
            onMouseLeave={() => setClientMouseOver(null)}
            sx={{
              borderRadius: 1,
              width: "90%",
              margin: "auto",
            }}
          >
            <ListItemIcon>
              <ClientAvatar
                initials={item.initials}
                alias={item.alias}
                isOffline={item.unregistered === 1}
              />
            </ListItemIcon>
            <ListItemText
              primary={
                <div style={{ display: "flex", gap: 8 }}>
                  <Typography noWrap>{item.alias}</Typography>
                  {item.new ? (
                    <Chip
                      color="secondary"
                      label={t("navigation.clients.new")}
                      size="small"
                    />
                  ) : null}
                </div>
              }
              //secondary={"Compliance: " + item.compliance}
              secondary={
                testDevice?.id === item.id ? t("navigation.clients.me") : ""
              }
            />
            {clientMouseOver?.id === item.id &&
            clientOptions(item).length > 0 ? (
              <ListItemSecondaryAction>
                <IconButton
                  size="small"
                  sx={{ padding: 1 }}
                  onClick={(event) => handleOpenClientMenu(event, item)}
                >
                  <MoreVertIcon fontSize="small" />
                </IconButton>
              </ListItemSecondaryAction>
            ) : null}
          </ListItem>
        ))}
        <InviteButton />
        {!testDevice && projectId === 0 ? <PairButton /> : null}
      </List>
    );
  if (clients?.length === 0)
    return (
      <Box>
        <Box padding={1} />
        <Typography sx={{ paddingLeft: 2 }}>
          {t("navigation.clients.empty")}
        </Typography>
        <Box padding={1} />
        <InviteButton />
        {!testDevice && projectId === 0 ? <PairButton /> : null}
      </Box>
    );
  return <div></div>;
}

export default ClientList;
